import { Professional, ProfessionalLicense, ProfessionalSpecialty } from '@medprev-web-app/api-graphql-client';

import { SchedulesSearchProfessionalLicenses } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { ReviewerSpecialty } from '@/types/post-reviewer.types';
import { Gender } from '@/types/auth-base.types';
import { FEMININE_HONORIFICS, MASCULINE_HONORIFICS } from '@/utils/formatters/professional-name';

export const CRM_COUNCIL_INITIALS = 'CRM';

export function hasAnyLicenseWithCRM(professional: { licenses?: ProfessionalLicense[] }): boolean {
  return professional.licenses?.some((license) => license.certificationBodyInitials === CRM_COUNCIL_INITIALS) || false;
}

export function hasGeneralistSpecialty(professional: { specialties?: ProfessionalSpecialty[] | null }): boolean {
  return professional.specialties?.some((specialty) => specialty.name?.toLowerCase() === 'médico generalista') || false;
}

export function isCrmLicense(license: SchedulesSearchProfessionalLicenses): boolean {
  return license.councilInitials === CRM_COUNCIL_INITIALS;
}

export const RQE_EXEMPT_SPECIALTIES = [
  'médico generalista',
  'clínico geral',
  'clínico geral / clínica médica',
  'clínico geral ou clínica médica',
] as const;

const normalizeSpecialtyName = (name: string): string => name.trim().toLowerCase().replace(/\s+/g, ' ');

export const isRqeExemptSpecialty = (specialtyName?: string): boolean => {
  const normalized = normalizeSpecialtyName(specialtyName ?? '');

  return RQE_EXEMPT_SPECIALTIES.some((exempt) => exempt === normalized);
};

export interface ProfessionalCrmSuffixContext {
  gender?: string;
  honorific?: string;
}

export const getProfessionalCrmSuffix = ({ gender, honorific }: ProfessionalCrmSuffixContext): string => {
  const normalizedGender = gender?.toUpperCase();

  if (normalizedGender === Gender.MALE) {
    return 'MÉDICO';
  }

  if (normalizedGender === Gender.FEMALE) {
    return 'MÉDICA';
  }

  const normalizedHonorific = honorific?.trim().toLowerCase();

  if (MASCULINE_HONORIFICS.some((masculine) => masculine.toLowerCase() === normalizedHonorific)) {
    return 'MÉDICO';
  }

  if (FEMININE_HONORIFICS.some((feminine) => feminine.toLowerCase() === normalizedHonorific)) {
    return 'MÉDICA';
  }

  return 'MÉDICO (A)';
};

interface ProfessionalLicenseSource {
  certificationBodyInitials?: string | null;
  licenseRecord?: string | null;
  licenseState?: string | null;
}

interface ProfessionalSpecialtySource {
  name?: string | null;
  specialistQualificationRecord?: string | null;
  isDisplayed?: boolean | null;
}

export interface ProfessionalLicenseRowsSource {
  licenses?: ProfessionalLicenseSource[] | null;
  specialties?: ProfessionalSpecialtySource[] | null;
}

export function mapProfessionalLicenseRows(
  professional: ProfessionalLicenseRowsSource | null,
): SchedulesSearchProfessionalLicenses[] {
  const licenses = professional?.licenses ?? [];
  const license = licenses.find((current) => current.certificationBodyInitials === CRM_COUNCIL_INITIALS) ?? licenses[0];

  if (!license) {
    return [];
  }

  const councilInitials = license.certificationBodyInitials ?? '';
  const registration = {
    councilInitials,
    professionalLicenseRecord: license.licenseRecord ?? '',
    professionalLicenseState: license.licenseState ?? '',
  };

  const specialties = councilInitials === CRM_COUNCIL_INITIALS ? (professional?.specialties ?? []) : [];

  if (specialties.length === 0) {
    return [{ ...registration, specialistQualificationRecord: '', specialtyName: '', isDisplayed: false }];
  }

  return specialties.map((specialty) => ({
    ...registration,
    specialistQualificationRecord: specialty.specialistQualificationRecord ?? '',
    specialtyName: specialty.name ?? '',
    isDisplayed: specialty.isDisplayed ?? false,
  }));
}

export const formatLicenseInfo = (license: SchedulesSearchProfessionalLicenses, suffix?: string): string => {
  const { councilInitials, professionalLicenseRecord, professionalLicenseState } = license;
  const registration = `${councilInitials} ${professionalLicenseRecord} – ${professionalLicenseState}`;

  return suffix ? `${registration} • ${suffix}` : registration;
};

export const specialtyLineDisplay = (license: SchedulesSearchProfessionalLicenses): string => {
  const { specialtyName, specialistQualificationRecord } = license;

  if (specialistQualificationRecord) {
    return `${specialtyName} – RQE ${specialistQualificationRecord}`;
  }

  if (isRqeExemptSpecialty(specialtyName)) {
    return specialtyName;
  }

  return `${specialtyName} – Não possui RQE para ${specialtyName.toLocaleUpperCase()}`;
};

export const blogRQEDisplay = (specialty: ReviewerSpecialty, hasCrm: boolean): string => {
  const { name, specialistQualificationRecord } = specialty;
  const isGeneralist = name?.toLowerCase() === 'médico generalista';
  if (isGeneralist) {
    return 'Médico Generalista';
  }

  if (!specialistQualificationRecord && hasCrm) {
    return `${name} - Não possui RQE para ${name.toLocaleUpperCase()}`;
  }

  return `${name} - RQE ${specialistQualificationRecord}`;
};

export const formatProfessionalLicenses = (licenses: ProfessionalLicense[]): string => {
  return licenses
    .map((license) => `${license.certificationBodyInitials}/${license.licenseState} - ${license.licenseRecord}`)
    .join(', ');
};

export const formatExperiences = (professional: Professional): string[] => {
  const hasCRM = hasAnyLicenseWithCRM(professional);
  const isGeneralist = hasGeneralistSpecialty(professional);
  const nonGeneralistSpecialties = professional?.specialties?.filter(
    (specialty) => specialty?.name?.toLowerCase() !== 'médico generalista',
  );

  const formatted = nonGeneralistSpecialties
    ?.map((specialty) => {
      const rqe = specialty.specialistQualificationRecord;

      if (hasCRM && !rqe) {
        return `${specialty.name} - Não possui RQE para ${specialty.name!.toLocaleUpperCase()}`;
      }
    })
    .filter(Boolean) as string[];

  if (isGeneralist) {
    formatted.unshift('Médico Generalista');
  }

  return formatted;
};

export const formatSpecialties = (professional: Professional): string[] => {
  const { specialties } = professional;

  if (!specialties || specialties.length === 0) {
    return [];
  }

  const hasCRM = hasAnyLicenseWithCRM(professional);
  const isGeneralist = hasGeneralistSpecialty(professional);

  const nonGeneralistSpecialties = specialties.filter(
    (specialty) => specialty.name!.toLowerCase() !== 'médico generalista',
  );

  const formatted = nonGeneralistSpecialties.map((specialty) => {
    if (!hasCRM) {
      return specialty.name;
    }

    const rqe = specialty.specialistQualificationRecord;
    return `${specialty.name} - ${rqe ? `RQE ${rqe}` : 'sem RQE'}`;
  });

  if (isGeneralist) {
    formatted.unshift('Médico Generalista');
  }

  return formatted as string[];
};

export function formatReviewerSpecialties(specialties: ReviewerSpecialty[] | undefined, hasCrm: boolean): string {
  if (!specialties || specialties.length === 0) {
    return '';
  }
  return specialties
    .map((specialty) => blogRQEDisplay(specialty, hasCrm))
    .filter(Boolean)
    .join(', ');
}
